import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useDuckDB } from '../../../../contexts/DuckDBContext';
import { getPresignedUrlApi } from '../../../../api\'s/s3Api';


export const useS3WithPresignedUrls = () => {
  const { connection, executeQuery, registerFile, isInitialized: isReady } = useDuckDB();

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Get sample data - FIXED VERSION
  const getSampleData = useCallback(
    async (s3Path, workflowId, secretName, options = {}, type = "aws") => {
      const { sampleSize = 10 } = options;

      if (!isReady || !connection) {
        enqueueSnackbar('DB not ready', { variant: 'error' });
        return null;
      }

      setLoading(true);

      try {
        // 🔹 Step 1: Get pre-signed URL
        const response = await getPresignedUrlApi({
          path: s3Path,
          workflow_id: workflowId,
          secretName,
          type,
        });

        const { url } = response.data;

        // 🔹 Step 2: Fetch CSV from S3
        const fetchResponse = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            Range: 'bytes=0-2097152', // 2MB (safe size)
          },
        });

        if (!fetchResponse.ok) {
          throw new Error(`HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`);
        }

        const csvText = await fetchResponse.text();

        // 🔹 Step 3: Register file in DuckDB
        const fileName = `sample_${Date.now()}.csv`;
        await registerFile(fileName, csvText);

        // 🔹 Step 4: Create temp view
        await executeQuery(`
          CREATE OR REPLACE TEMP VIEW temp_sample AS
          SELECT *
          FROM read_csv_auto(
            '${fileName}',
            HEADER=true,
            AUTO_DETECT=true,
            ALL_VARCHAR=true,
            IGNORE_ERRORS=true
          )
        `);

        // 🔹 Step 5: Get column metadata (REAL DuckDB types)
        const columnMeta = await executeQuery(`DESCRIBE temp_sample`);

        const columns = columnMeta.map((col) => ({
          name: col.column_name,
          type: col.column_type,
        }));

        // 🔹 Step 6: Get sample rows
        const rawRows = await executeQuery(`
        SELECT * FROM temp_sample
        LIMIT ${sampleSize}
      `);

        const sampleRows = rawRows.map((row) => {
          const entries = Object.keys(row).map((key) => {
            const value = row[key];
            return [
              key,
              typeof value === 'bigint' ? value.toString() : value,
            ];
          });
          return Object.fromEntries(entries);
        });

        setLoading(false);

        const tables = await executeQuery(`SHOW TABLES`);
        console.log('DuckDB Tables:', tables);

        return {
          columns, // [{name, type}]
          sampleRows, // array of rows
          rowCount: sampleRows.length,
          method: 'duckdb',
        };
      } catch (error) {
        console.error('❌ Error in getSampleData:', error);

        let errorMessage = error.message;

        if (error.message.includes('403')) {
          errorMessage = 'Access forbidden. Check S3 permissions.';
        } else if (error.message.includes('404')) {
          errorMessage = 'File not found in S3.';
        } else if (error.message.includes('Binder Error')) {
          errorMessage = `DuckDB query error: ${error.message}`;
        }

        enqueueSnackbar(`Failed: ${errorMessage}`, {
          variant: 'error',
        });

        setLoading(false);

        // 🔹 Fallback: Try to get columns from session storage
        try {
          const store = JSON.parse(sessionStorage.getItem('allNodes')) || [];
          const fallbackNode = store.find(
            (node) => node.params?.path === s3Path && node.params?.select_cols
          );

          if (fallbackNode) {
            const fallbackColumns = fallbackNode.params.select_cols
              .split(',')
              .map((col) => ({
                name: col.trim().replace(/[`'"]/g, ''),
                type: 'VARCHAR',
              }));

            return {
              columns: fallbackColumns,
              sampleRows: [],
              rowCount: 0,
              error: errorMessage,
            };
          }
        } catch (e) {
          console.log('No fallback columns found');
        }

        return null;
      }
    },
    [isReady, connection, executeQuery, registerFile, enqueueSnackbar]
  );

  // Profile function with better error handling
  const profileWithPresignedUrl = useCallback(
    async (s3Path, workflowId) => {
      if (!isReady || !connection) return null;

      setLoading(true);

      try {
        const sampleData = await getSampleData(s3Path, workflowId, {
          sampleSize: 100,
        });

        if (!sampleData?.data?.length) {
          throw new Error('No sample data received');
        }

        console.log(`📊 Profiling ${sampleData.data.length} rows...`);

        // Convert to CSV for profiling
        const csvContent = convertToCSV(sampleData.data);
        await registerFile('profile_sample.csv', csvContent);

        // Run basic profiling query
        const profileQuery = `
        WITH sample AS (
          SELECT * FROM read_csv_auto('profile_sample.csv')
        )
        SELECT 
          COUNT(*) as total_rows,
          COUNT(*) as total_columns
        FROM sample
      `;

        const profile = await executeQuery(profileQuery);

        setLoading(false);

        return {
          sample: sampleData,
          profile: profile[0],
          columns: sampleData.columns,
          timestamp: new Date().toISOString(),
        };
      } catch (error) {
        console.error('Profiling failed:', error);
        enqueueSnackbar(`Profiling failed: ${error.message}`, {
          variant: 'error',
        });
        setLoading(false);
        return null;
      }
    },
    [isReady, connection, executeQuery, registerFile, getSampleData]
  );

  // Helper: Convert to CSV
  const convertToCSV = (data) => {
    if (!data.length) return '';

    const headers = Object.keys(data[0]);
    const csvRows = [];

    csvRows.push(headers.join(','));

    data.forEach((row) => {
      const values = headers.map((header) => {
        const value = row[header]?.toString() || '';
        // Escape commas and quotes
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvRows.push(values.join(','));
    });

    return csvRows.join('\n');
  };

  return {
    getSampleData,
    profileWithPresignedUrl,
    loading,
    progress,
  };
};
